import { GLOBAL_CONTEXT_KEY, contextChain } from './resolveContext';
import type { FaqGroup, HelpContext, HelpFaq } from './types';

export const FAQ_VISIBLE_CAP = 12;

export interface SelectFaqOptions {
  includeDrafts?: boolean;
}

function activeGroupLabel(context: HelpContext): string {
  if (context.key === GLOBAL_CONTEXT_KEY) return 'About DVA';
  if (context.parent === GLOBAL_CONTEXT_KEY) return 'Questions about this page';
  return `Questions about the ${context.label}`;
}

function laterGroupLabel(context: HelpContext): string {
  return context.key === GLOBAL_CONTEXT_KEY ? 'About DVA' : context.label;
}

/**
 * Section FAQs, then the parent page's, then global — each ordered by `order`.
 * The first group is always the active context, even when it has no FAQs; later
 * groups are dropped when empty.
 */
export function selectFaqGroups(
  contextKey: string,
  faqs: HelpFaq[],
  contexts: HelpContext[],
  options: SelectFaqOptions = {},
): FaqGroup[] {
  const visible = options.includeDrafts ? faqs : faqs.filter((faq) => faq.status === 'published');
  const chain = contextChain(contextKey, contexts);

  return chain
    .map((context, index) => ({
      key: context.key,
      label: index === 0 ? activeGroupLabel(context) : laterGroupLabel(context),
      faqs: visible
        .filter((faq) => faq.context_key === context.key)
        .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id)),
    }))
    .filter((group, index) => index === 0 || group.faqs.length > 0);
}

export function flattenGroups(groups: FaqGroup[]): HelpFaq[] {
  return groups.flatMap((group) => group.faqs);
}

/** Truncate across groups in order so at most `limit` questions are visible. */
export function capGroups(groups: FaqGroup[], limit: number = FAQ_VISIBLE_CAP): { groups: FaqGroup[]; remaining: number } {
  let budget = limit;
  let remaining = 0;
  const capped: FaqGroup[] = [];
  groups.forEach((group, index) => {
    const take = Math.max(0, Math.min(budget, group.faqs.length));
    remaining += group.faqs.length - take;
    budget -= take;
    if (index === 0 || take > 0) capped.push({ ...group, faqs: group.faqs.slice(0, take) });
  });
  return { groups: capped, remaining };
}
